/**
 * nav-model.ts — the PMS Shell v4 navigation model (SCRUM-297 / MENU-1).
 *
 * `NAV_V4` is the canonical menu model ported verbatim from the design
 * handoff (`design_handoff_menu/source/pms-v4-shell.jsx`): 13 top-level
 * entries, 4 of which are expandable groups (Operations / Channel
 * Management / Accounting / Integrations), with a single level of nesting.
 *
 * Dependency-free by design (the plan's lint/compliance rule): the ONLY
 * import is the `IconName` *type* from `@sparta/ui-kit`, so the model can
 * be consumed by tests and by the sidebar/topbar without dragging in React
 * or any runtime. The shipped Sidebar/TopBar behavior lands in MENU-2/3;
 * this module only owns the data + the flat mobile-drawer projection.
 *
 * `navToMobileItems` is intentionally a FLAT list for MENU-1 — the shared
 * `MobileNavDrawer` (entities/navigation) is fed top-level rows, with
 * not-yet-routed entries rendered as disabled rows (the no-regression
 * carry-over from DashboardPage's `PMS_NAV_ITEMS`). A group child that already
 * resolves to a shipped route (today: `ops-users` → `/users`) is surfaced as a
 * flat enabled row so it stays reachable; the nested group expand/collapse
 * affordance defers to MENU-2.
 */
import type { MobileNavItem } from '../../entities/navigation/MobileNavDrawer.js';
// `NavNode` is the SHARED nav contract — it lives with the `SidebarV4`
// component in the entities tier (SCRUM-298 / MENU-2), not here. We import the
// TYPE from the sidebar's deep barrel (no parent `entities/navigation` barrel
// is added — same deep-import style `PmsShellV4` uses for `MobileNavDrawer`).
// `NAV_V4` + `NAV_V4_ROUTABLE` + the helpers stay PMS-portal-local in this file
// (the entities tier must not import a `widgets` slice, ADR `frontend-0001`).
import type { NavNode } from '../../entities/navigation/sidebar-v4/index.js';

export type { NavNode };

/**
 * The canonical PMS Shell v4 menu model. Order is load-bearing (the sidebar
 * renders top-to-bottom in this order) and asserted by the spec.
 */
export const NAV_V4: readonly NavNode[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'grid' },
  { id: 'inbox', label: 'Inbox', icon: 'inbox' },
  { id: 'calendar', label: 'Multi Calendar', icon: 'calendar' },
  { id: 'lab', label: 'Lab', icon: 'bolt' },
  { id: 'agency', label: 'Agency', icon: 'sparkles' },
  { id: 'reservations', label: 'Reservations', icon: 'receipt' },
  { id: 'properties', label: 'Properties', icon: 'building' },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'checks',
    children: [
      { id: 'ops-msg', label: 'Message Automation' },
      { id: 'ops-tasks', label: 'Tasks' },
      { id: 'ops-users', label: 'Users Manager' },
      { id: 'ops-insights', label: 'Insights' },
    ],
  },
  {
    id: 'channels',
    label: 'Channel Management',
    icon: 'swap',
    children: [
      { id: 'ch-channels', label: 'Channels' },
      { id: 'ch-rate', label: 'Rate Strategy' },
      { id: 'ch-markups', label: 'Markups' },
      { id: 'ch-promos', label: 'Promotions' },
      { id: 'ch-coupons', label: 'Coupons' },
    ],
  },
  { id: 'crm', label: 'CRM', icon: 'users' },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: 'chart',
    children: [
      { id: 'ac-expense', label: 'Expense Report' },
      { id: 'ac-recurring', label: 'Recurring Expenses' },
      { id: 'ac-fees', label: 'Fees' },
      { id: 'ac-taxes', label: 'Taxes' },
      { id: 'ac-comm', label: 'Commissions' },
      { id: 'ac-revenue', label: 'Revenue' },
      { id: 'ac-analytics', label: 'Analytics' },
      { id: 'ac-payments', label: 'Payments' },
      { id: 'ac-locking', label: 'Locking Period' },
      { id: 'ac-vendors', label: 'Vendors' },
      { id: 'ac-journal', label: 'Posting Journal' },
      { id: 'ac-disburse', label: 'Disbursements' },
      { id: 'ac-reports', label: 'Reports' },
      { id: 'ac-owner', label: 'Owner Statements' },
      { id: 'ac-check', label: 'Check Register' },
      { id: 'ac-bank', label: 'Bank Reconciliation' },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'grid',
    children: [
      { id: 'int-market', label: 'Marketplace' },
      { id: 'int-tokens', label: 'Tokens' },
    ],
  },
  { id: 'account', label: 'Account', icon: 'user' },
];

/**
 * The subset of nav ids — top-level OR group child — that resolve to a shipped
 * route today. Everything else renders as a disabled row in the mobile drawer
 * (and the sidebar's onSelect no-ops) until its module FE shell lands in a
 * later ticket. Mirrors the routes registered under `/` in `app/router.tsx`.
 *
 * `ops-users` → `/users`: the Users Manager surface (SCRUM-25) is already
 * shipped and routed. The design nests it under the Operations group, but it
 * is a real, currently-reachable route — so the shell swap must keep it
 * honestly navigable rather than strand it behind a dead group row.
 *
 * `agency` → `/agency`: the Agency leaf is the one dual leaf — it both routes
 * AND expands its agent quick-list. MENU-6 / SCRUM-302 wires the route to an
 * honest `AdminComingSoonPanel` placeholder: `ai.*` carries no authz slug yet,
 * so the route is `authenticated`, graduating to a real `modules/ai/` surface +
 * agent backend in a later ticket (see the MENU-5 BE-need).
 *
 * Kept here (not in `PmsShellV4`) so the mapping + the flat drawer
 * projection live in one dependency-free place and stay testable.
 */
export const NAV_V4_ROUTABLE: Readonly<Record<string, string>> = {
  dashboard: '/',
  reservations: '/reservations',
  agency: '/agency',
  properties: '/properties',
  'ops-users': '/users',
  account: '/account',
};

/**
 * Resolve a router pathname to the top-level `NAV_V4` id that should read as
 * active. Falls back to `dashboard` for `/` and anything unmapped — matching
 * DashboardPage's `pathnameToLeftMenuActive` behavior.
 */
export function pathnameToNavV4Active(pathname: string): string {
  // The Reservations surface — `/reservations`, plus `/reservations/new` and
  // `/reservations/:id` — highlights the top-level `reservations` nav id.
  if (pathname === '/reservations' || pathname.startsWith('/reservations/')) {
    return 'reservations';
  }
  if (pathname === '/properties' || pathname.startsWith('/properties/')) {
    return 'properties';
  }
  // The Agency leaf (MENU-6 / SCRUM-302) — the one leaf that both routes to
  // /agency AND expands its agent quick-list; highlight it on its own route.
  if (pathname === '/agency' || pathname.startsWith('/agency/')) {
    return 'agency';
  }
  // The Users Manager surface — `/users`, plus its `/users/invitations`,
  // `/users/invite`, `/users/:userId` subtree — is the `ops-users` child of
  // the Operations group. Returning the CHILD id highlights its row AND
  // auto-opens the parent group in SidebarV4 (via `ownerGroupId`).
  if (pathname === '/users' || pathname.startsWith('/users/')) {
    return 'ops-users';
  }
  if (pathname === '/account' || pathname.startsWith('/account/')) {
    return 'account';
  }
  return 'dashboard';
}

/**
 * Project `NAV_V4` into the flat `MobileNavItem[]` the shared
 * `MobileNavDrawer` consumes. One row per top-level node — routable ids get
 * their real `href`, the rest render disabled (`href: '#'`, `disabled: true`)
 * — the same no-regression contract DashboardPage shipped via `PMS_NAV_ITEMS`.
 *
 * Group nodes stay collapsed to a single (disabled) top-level row in MENU-1
 * (the nested expand/collapse affordance defers to MENU-2). The ONE exception
 * is a group child that already resolves to a shipped route: it is surfaced as
 * a flat enabled row right after its parent so the route stays honestly
 * reachable on mobile rather than stranded behind a dead group row. Today that
 * is `ops-users` → `/users`; dashboard / properties / account are the routable
 * top-level destinations.
 */
export function navToMobileItems(
  nodes: readonly NavNode[] = NAV_V4,
): readonly MobileNavItem[] {
  const items: MobileNavItem[] = [];
  for (const node of nodes) {
    const href = NAV_V4_ROUTABLE[node.id];
    // Top-level row: a routable leaf gets its real href; a group row or a
    // not-yet-built leaf renders as a disabled placeholder.
    items.push({
      id: node.id,
      label: node.label,
      // Top-level nodes always carry an icon; the fallback satisfies the type.
      icon: node.icon ?? 'grid',
      href: href ?? '#',
      disabled: href === undefined,
    });
    // Surface any ROUTABLE group child as a flat enabled row immediately after
    // its parent (children inherit the parent group's icon for the flat row).
    for (const child of node.children ?? []) {
      const childHref = NAV_V4_ROUTABLE[child.id];
      if (childHref !== undefined) {
        items.push({
          id: child.id,
          label: child.label,
          icon: node.icon ?? 'grid',
          href: childHref,
          disabled: false,
        });
      }
    }
  }
  return items;
}
